import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

/**
 * Manages modal dialog state by tracking open modal identifiers.
 *
 * Provides methods to open or close modals by key, and observables to reactively
 * determine whether specific modals or any modal is currently open.
 */

@Injectable({ providedIn: 'root' })
export class ModalManagerService {
  private readonly openKeys$ = new BehaviorSubject<Set<string>>(new Set());

  /**
   * Marks the modal identified by the provided key as open by adding it to the currently tracked set of open modal keys.
   *
   * @param key - Unique identifier representing the modal to open.
   */
  open(key: string): void {
    const next = new Set(this.openKeys$.value);
    next.add(key);
    this.openKeys$.next(next);
  }

  /**
   * Closes the modal identified by the provided key by removing it from the set of open modals.
   * @param key Unique identifier of the modal to close.
   */
  close(key: string): void {
    const next = new Set(this.openKeys$.value);
    next.delete(key);
    this.openKeys$.next(next);
  }

  /**
   * Emits `true` whenever the modal identified by the provided key is currently open,
   * and `false` otherwise. The observable only emits when the open state changes.
   *
   * @param key - Unique identifier of the modal whose open state should be tracked.
   * @returns Observable that reflects the open state of the specified modal without duplicate consecutive values.
   */
  isOpen$(key: string): Observable<boolean> {
    return this.openKeys$.pipe(
      map((set) => set.has(key)),
      distinctUntilChanged()
    );
  }

  /**
   * Emits `true` whenever at least one modal is open and `false` when all are closed.
   * The emitted value only changes when the open state toggles.
   */
  isAnyOpen$(): Observable<boolean> {
    return this.openKeys$.pipe(
      map((set) => set.size > 0),
      distinctUntilChanged()
    );
  }
}
